// components/PrintableReport.jsx
import { forwardRef } from "react";
import { Home } from "react-feather";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import styles from "./printablereport.module.css";
import KpiCard from "./KpiCard";
import SkylineBar from "./SkylineBar";
import TierRow from "./TierRow";
import GoalRow from "./GoalRow";

const GOAL_COLORS = ["#f48272", "#e8a93c", "#7c72cc", "#1d9e75", "#f97316"];

function goalStatus(pct) {
    if (pct >= 100) return { label: "Completed", cls: "statusCompleted" };
    if (pct >= 70) return { label: "On track", cls: "statusOnTrack" };
    if (pct >= 40) return { label: "In progress", cls: "statusProgress" };
    return { label: "Starting", cls: "statusStarting" };
}

function TenantGoalPrintRow({ goals }) {
    return (
        <div className={styles.printGoalsWrapper}>
            {goals.map((g, idx) => {
                const color = GOAL_COLORS[idx % GOAL_COLORS.length];
                const status = goalStatus(g.pct);
                return (
                    <div key={idx} className={styles.printGoalCard}>
                        <div className={styles.printGoalInfo}>
                            <span className={styles.printGoalDot} style={{ background: color }} />
                            <span className={styles.printGoalName}>{g.label}</span>
                        </div>
                        <div className={styles.printGoalBarBg}>
                            <div className={styles.printGoalBarFill} style={{ width: `${g.pct}%`, background: color }} />
                        </div>
                        <div className={styles.printGoalFooter}>
                            <span className={styles.printGoalPct}>{g.pct}%</span>
                            <span className={`${styles.printGoalStatusBadge} ${styles[status.cls]}`}>{status.label}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function TenantRow({ avatar, name, sub, scoreOld, scoreNew, pts, ptsType, tierLabel, tierCls, goals }) {

    const proxyUrl = (originalUrl) => `${process.env.REACT_APP_TENANT_API_URL}v1/credit-builder/proxy-image?url=${encodeURIComponent(originalUrl)}`;

    return (<>
        <TableRow className={styles.tenantRow}>
            <TableCell className={styles.TableBodyText}>
                <div className={styles.AvatarSec}>
                    <div className={styles.ProfileImg}>
                        <img src={avatar ? proxyUrl(avatar) : ''} alt={name} />
                    </div>
                    <div className={styles.TenantSec}>
                        <span>{name}</span>
                        <p>{sub}</p>
                    </div>
                </div>
            </TableCell>
            <TableCell className={styles.TableBodyText}>
                <div className={styles.scorePair}>
                    <span className={styles.scoreOld}>{scoreOld}</span>
                    <span className={styles.scoreArrow}>→</span>
                    <span className={styles.scoreNew}>{scoreNew}</span>
                </div>
            </TableCell>
            <TableCell className={styles.TableBodyText}>
                <span className={`${styles.ptsBadge} ${ptsType === "up" ? styles.ptsUp : styles.ptsFlat}`}>
                    {pts}
                </span>
            </TableCell>
            <TableCell className={styles.TableBodyText}>
                <span className={`${styles.tierChip} ${styles[tierCls]}`}>{tierLabel}</span>
            </TableCell>
        </TableRow>
        {goals && goals.length > 0 && (
            <TableRow className={styles.goalsRow}>
                <TableCell colSpan={4} className={styles.goalsCell}>
                    <div className={styles.goalsContainer}>
                        <p className={styles.goalsTitle}>Financial Goals</p>
                        <TenantGoalPrintRow goals={goals} />
                    </div>
                </TableCell>
            </TableRow>
        )}
    </>);
}

const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

// Pure presentational component — no state, no fetching, no interactivity.
// Rendered off-screen and captured by html2canvas for the PDF export.
const PrintableReport = forwardRef(
    ({ propertyName, dateRangeLabel, kpiData, chartData, tierList, goals, residents }, ref) => {
        const residentChunks = residents.length > 0 ? chunkArray(residents, 3) : [[]];

        return (
            <div ref={ref} className={styles.printContainer}>
                {/* Page 1: Overview */}
                <div className={`${styles.printPage} printPage`}>
                    {/* Purple header card */}
                    <div className={styles.reportHeader}>
                        <div className={styles.headerTop}>
                            <div className={styles.logoSec}>
                                <div className={styles.logoIcon}>
                                    <Home size={18} color="#fff" />
                                </div>
                                <span className={styles.logoText}>PropertyDek</span>
                            </div>
                        </div>
                        <div className={styles.headerBottom}>
                            <h1 className={styles.reportTitle}>{propertyName} — Credit to Wealth Report</h1>
                            <p className={styles.reportSubtitle}>
                                Resident credit score & financial goal progress · {dateRangeLabel}
                            </p>
                        </div>
                    </div>

                    <div className={styles.CenterBlockSec}>
                        <div className={styles.Row}>
                            <div className={styles.CashFlowSec}>

                                {/* KPI CARDS */}
                                <div className={styles.section}>
                                    <div className={styles.kpiGrid}>
                                        {kpiData.map((k, i) => <KpiCard key={i} {...k} />)}
                                    </div>
                                </div>

                                {/* SKYLINE CHART */}
                                <div className={styles.section}>
                                    <div className={styles.sectionHead}>
                                        <p className={styles.sectionTitle}>Cohort credit score trend</p>
                                        <p className={styles.sectionNote}>Average score across all enrolled residents, by month</p>
                                    </div>
                                    <div className={styles.chartCard}>
                                        <div className={styles.skyline}>
                                            {chartData.map((d, i) => <SkylineBar key={i} {...d} />)}
                                        </div>
                                    </div>
                                </div>

                                {/* TIER + GOAL BREAKDOWN */}
                                <div className={styles.section}>
                                    <div className={styles.twoCol}>
                                        <div className={styles.panel}>
                                            <p className={styles.panelTitle}>Credit score tier distribution</p>
                                            {tierList.map((t, i) => <TierRow key={i} {...t} />)}
                                        </div>
                                        <div className={styles.panel}>
                                            <p className={styles.panelTitle}>Financial goal breakdown</p>
                                            {goals.map((g, i) => <GoalRow key={i} {...g} />)}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* Resident Details Pages */}
                {residentChunks.map((chunk, pageIdx) => (
                    <div key={pageIdx} className={`${styles.printPage} printPage`}>
                        <div className={styles.CenterBlockSec}>
                            <div className={styles.Row}>
                                <div className={styles.CashFlowSec}>
                                    {/* RESIDENT TABLE — chunked */}
                                    <div className={styles.OfferListSec}>
                                        <div className={styles.CashFlowCard}>
                                            <div className={styles.OfferCardHead}>
                                                <p className={styles.CardHeadTitle}>
                                                    Resident progress detail {residentChunks.length > 1 && `(Page ${pageIdx + 1} of ${residentChunks.length})`}
                                                </p>
                                            </div>

                                            <div className={styles.OfferTableSec}>
                                                <TableContainer className={styles.TableDiv}>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell className={styles.TableHead} style={{ width: 220 }}>Resident</TableCell>
                                                                <TableCell className={styles.TableHead} style={{ width: 140 }}>Baseline → current</TableCell>
                                                                <TableCell className={styles.TableHead} style={{ width: 80 }}>Pts</TableCell>
                                                                <TableCell className={styles.TableHead} style={{ width: 100 }}>Tier</TableCell>
                                                            </TableRow>
                                                        </TableHead>

                                                        <TableBody>
                                                            {chunk.length === 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={4} className={styles.TableBodyText}>
                                                                        No residents with credit score or goal activity yet.
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                            {chunk.map((r) => <TenantRow key={r.id} {...r} />)}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }
);

PrintableReport.displayName = "PrintableReport";

export default PrintableReport;